import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { installAnalytics } from '@/lib/analytics';

// =====================================================================
// CookieBanner — низ страницы при первом визите. Сохраняет выбор в
// localStorage и больше не показывается. «Настроить» открывает Radix-диалог
// с переключателями по категориям (обязательные / аналитика / маркетинг).
// =====================================================================

const STORAGE_KEY = 'aniphys_cookie_consent';
const CONSENT_VERSION = 2;

type Consent = {
  version: number;
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
};

function readConsent(): Consent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Consent>;
    if (typeof parsed !== 'object' || parsed === null) return null;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (typeof parsed.timestamp !== 'number') return null;
    return {
      version: CONSENT_VERSION,
      necessary: true,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      timestamp: parsed.timestamp,
    };
  } catch {
    return null;
  }
}

function writeConsent(c: Omit<Consent, 'version' | 'timestamp'>) {
  if (typeof window === 'undefined') return;
  const payload: Consent = { ...c, version: CONSENT_VERSION, timestamp: Date.now() };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage может быть недоступен (private mode) — без падения.
  }
}

function Toggle({
  label,
  description,
  value,
  onChange,
  locked,
}: {
  label: string;
  description: string;
  value: boolean;
  onChange?: (v: boolean) => void;
  locked?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-[var(--text)]">{label}</div>
        <div className="text-xs text-[var(--text-muted)] mt-0.5 leading-relaxed">{description}</div>
      </div>
      <button
        type="button"
        aria-label={label}
        aria-pressed={value}
        disabled={locked}
        onClick={() => onChange?.(!value)}
        className="relative shrink-0 h-6 w-10 rounded-full transition-colors disabled:cursor-not-allowed"
        style={{
          background: value ? 'var(--accent)' : 'var(--surface-2)',
          border: '1px solid var(--border)',
        }}
      >
        <span
          className="absolute top-[1px] h-[18px] w-[18px] rounded-full bg-white transition-[left]"
          style={{
            left: value ? '18px' : '2px',
          }}
        />
      </button>
    </div>
  );
}

export function CookieBanner() {
  const [decided, setDecided] = useState<boolean>(true); // optimistic — hide until effect resolves
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = readConsent();
    setDecided(existing !== null);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('cookie-banner-visible', !decided);
    return () => document.documentElement.classList.remove('cookie-banner-visible');
  }, [decided]);

  function commit(next: { analytics: boolean; marketing: boolean }) {
    writeConsent({ necessary: true, analytics: next.analytics, marketing: next.marketing });
    if (next.analytics) installAnalytics();
    setDecided(true);
    setCustomizeOpen(false);
  }

  if (decided) return null;

  return (
    <>
      <div
        data-cookie-banner
        role="region"
        aria-label="Согласие на использование cookie"
        className="fixed bottom-0 inset-x-0 z-[100] p-2 md:p-5"
        style={{
          pointerEvents: 'none',
          paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))',
          paddingLeft: 'max(0.5rem, env(safe-area-inset-left))',
          paddingRight: 'max(0.5rem, env(safe-area-inset-right))',
        }}
      >
        <div
          className="mx-auto max-w-[920px] rounded-xl p-3 md:p-5 shadow-elevated"
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border-strong)',
            pointerEvents: 'auto',
          }}
        >
          <div className="flex items-start gap-3 md:gap-4">
            <div
              className="hidden md:grid place-items-center shrink-0"
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: 'var(--surface-2)',
                color: 'var(--text)',
              }}
              aria-hidden
            >
              <Cookie className="size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-0.5 text-sm font-semibold text-[var(--text)] md:mb-1">
                Мы используем файлы cookie
              </div>
              <p className="text-xs leading-[1.4] text-[var(--text-muted)] md:text-[13px] md:leading-relaxed">
                <span className="md:hidden">Обязательные cookie — для работы. Остальные — только с согласия. Подробнее — в{' '}</span>
                <span className="hidden md:inline">Обязательные cookie нужны для работы сайта. Аналитические и маркетинговые подключаются с вашего согласия. Подробнее — в{' '}</span>
                <Link to="/legal/cookies" className="underline text-[var(--text)]">Политике cookies</Link>.
              </p>
              <div className="mt-2 grid grid-cols-2 gap-2 md:mt-3 md:flex md:flex-wrap">
                <Button
                  size="sm"
                  variant="accent"
                  className="w-full md:w-auto"
                  onClick={() => commit({ analytics: true, marketing: true })}
                >
                  Принять всё
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  className="w-full px-2 text-[11px] md:w-auto md:px-3 md:text-xs"
                  onClick={() => commit({ analytics: false, marketing: false })}
                >
                  Только обязательные
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="col-span-2 w-full md:col-auto md:w-auto"
                  onClick={() => {
                    setAnalytics(false);
                    setMarketing(false);
                    setCustomizeOpen(true);
                  }}
                >
                  Настроить
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
        <DialogContent>
          <DialogTitle>Настройки cookie</DialogTitle>
          <DialogDescription>
            Выберите категории, которые разрешаете использовать на aniphys.ru.
          </DialogDescription>
          <div className="mt-4 divide-y divide-[var(--border)]">
            <Toggle
              label="Обязательные"
              description="Авторизация, сессии и сохранение выбора в cookie-баннере. Без них сервис не работает — отключить нельзя."
              value
              locked
            />
            <Toggle
              label="Аналитические"
              description="Umami и Яндекс Метрика. Статистика посещений и учёт регистраций; Вебвизор выключен."
              value={analytics}
              onChange={setAnalytics}
            />
            <Toggle
              label="Маркетинговые"
              description="Сейчас не используются. Будут активированы только после повторного согласия."
              value={marketing}
              onChange={setMarketing}
            />
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCustomizeOpen(false)}>Отмена</Button>
            <Button variant="primary" onClick={() => commit({ analytics, marketing })}>
              Сохранить
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
